import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { DealDateInput } from "@/components/Workflow/QuickActions/CreateDeal/DealDateInput";
import { DealDynamicFields } from "@/components/Workflow/QuickActions/CreateDeal/DealDynamicFields";
import { DealTeam } from "@/components/Workflow/QuickActions/CreateDeal/DealTeam";
import {
  DEAL_PRIORITIES,
  isForbiddenError,
  parseApiErrorMessage,
  parseDateInput,
  type DealPersonOption,
  type DealPriority,
} from "@/components/Workflow/QuickActions/CreateDeal/create-deal-helpers";
import {
  initializeDynamicFields,
  validateDynamicFields,
} from "@/components/Workflow/QuickActions/CreateDeal/deal-dynamic-fields";
import {
  setAllMembers,
  toggleMember,
  toggleOwner,
  type DealTeamSelection,
} from "@/components/Workflow/QuickActions/CreateDeal/deal-team";
import { DynamicFieldsApi } from "@/services/api/dynamic-fields-api";
import { ProjectsApi } from "@/services/api/projects-api";
import { useAuth } from "@/services/auth/use-auth";
import type { TriyoDynamicField, TriyoProject } from "@/types/models";
import { formatLongCalendarDate } from "@/utils/calendar-date";
import {
  buildEditDealPayload,
  buildEditRoster,
  buildEditTeamSelection,
  editStateFingerprint,
  getDealId,
  isInitialProjectMember,
  isInitialProjectOwner,
  projectPriority,
  singleProjectOwnerId,
  toProjectDateInput,
} from "./deal-edit";
import "@/components/Workflow/QuickActions/CreateDeal/CreateDeal.scss";
import "./DealDetails.scss";

export interface DealDetailsProps {
  summary: TriyoProject;
  editable: boolean;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onUpdated: (project: TriyoProject) => void;
}

const STEPS = ["Deal Information", "Deal Team"] as const;
type PartialSaveStatus = "refreshed" | "refresh-failed";

function partialSaveMessage(status: PartialSaveStatus): string {
  return status === "refreshed"
    ? "Your other Deal changes were saved, but the ownership transfer could not be confirmed. The Deal has been refreshed so you can review its current owner before retrying."
    : "Your other Deal changes were saved, but the ownership transfer could not be confirmed. TRIYO could not refresh the Deal, so its current owner is unknown. Use Try again before retrying the transfer.";
}

function statusName(project: TriyoProject): string {
  if (project.StrStatus) return project.StrStatus;
  if (project.StatusName) return project.StatusName;
  if (project.Status && !/^\d+$/.test(project.Status)) return project.Status;
  if (project.StatusId === 0) return "Archived";
  if (project.StatusId === 2) return "Completed";
  return "Active";
}

function projectMemberNames(project: TriyoProject): string[] {
  const options = buildEditRoster(project, null);
  const selection = buildEditTeamSelection(project);
  return options.filter((option) => selection.memberKeys.has(option.key)).map((option) => option.name);
}

function projectOwnerNames(project: TriyoProject): string[] {
  const options = buildEditRoster(project, null);
  const selection = buildEditTeamSelection(project);
  return options.filter((option) => selection.ownerKeys.has(option.key)).map((option) => option.name);
}

function selectedUserOwnerId(options: DealPersonOption[], selection: DealTeamSelection): number | null {
  const owner = options.find((option) => !option.isGroup && selection.ownerKeys.has(option.key));
  return owner?.id ?? null;
}

function StepBars({ step }: { step: number }): JSX.Element {
  return (
    <div className="create-deal__step-bars" role="list" aria-label={`Step ${step} of ${STEPS.length}`}>
      {STEPS.map((label, index) => (
        <div
          key={label}
          role="listitem"
          aria-label={label}
          aria-current={index + 1 === step ? "step" : undefined}
          className={`create-deal__step-bar${index + 1 <= step ? " create-deal__step-bar--filled" : ""}`}
        />
      ))}
    </div>
  );
}

export function DealDetails({ summary, editable, onClose, onDirtyChange, onUpdated }: DealDetailsProps): JSX.Element {
  const { user } = useAuth();
  const requestRef = useRef(0);
  const projectId = getDealId(summary);

  const [project, setProject] = useState<TriyoProject | null>(null);
  const [options, setOptions] = useState<DealPersonOption[]>([]);
  const [team, setTeam] = useState<DealTeamSelection>({ memberKeys: new Set(), ownerKeys: new Set() });
  const [initialTeam, setInitialTeam] = useState<DealTeamSelection>({ memberKeys: new Set(), ownerKeys: new Set() });
  const [baselineFingerprint, setBaselineFingerprint] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [dynamicLoadError, setDynamicLoadError] = useState(false);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [priority, setPriority] = useState<DealPriority>("Medium");
  const [dynamicFields, setDynamicFields] = useState<TriyoDynamicField[]>([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [teamNotice, setTeamNotice] = useState("");
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [dynamicErrors, setDynamicErrors] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [partialSaveStatus, setPartialSaveStatus] = useState<PartialSaveStatus | null>(null);
  const [saved, setSaved] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [projectTransferType, setProjectTransferType] = useState<0 | 1>(0);

  const load = useCallback(async (): Promise<boolean> => {
    if (!projectId) {
      setLoading(false);
      setLoadError("This Deal does not have a valid id.");
      return false;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setLoadError("");
    setDynamicLoadError(false);
    try {
      const fullProject = await ProjectsApi.getProject(projectId);
      if (requestId !== requestRef.current) return false;

      let nextOptions = buildEditRoster(fullProject, null);
      if (editable) {
        const roster = await ProjectsApi.getUsersAndGroups();
        if (requestId !== requestRef.current) return false;
        nextOptions = buildEditRoster(fullProject, roster);
      }

      let fields: TriyoDynamicField[] = [];
      if (fullProject.ParentTemplateId) {
        try {
          fields = initializeDynamicFields(
            await DynamicFieldsApi.getProjectDynamicFields(fullProject.ParentTemplateId, projectId)
          );
        } catch {
          setDynamicLoadError(true);
        }
      }
      if (requestId !== requestRef.current) return false;

      const nextTeam = buildEditTeamSelection(fullProject);
      const nextName = fullProject.Name ?? "";
      const nextDescription = fullProject.Description ?? "";
      const nextStart = toProjectDateInput(fullProject.Start ?? fullProject.StartDate);
      const nextEnd = toProjectDateInput(fullProject.End ?? fullProject.EndDate ?? fullProject.DueDate);
      const nextPriority = projectPriority(fullProject);

      setProject(fullProject);
      setOptions(nextOptions);
      setTeam(nextTeam);
      setInitialTeam(nextTeam);
      setName(nextName);
      setDescription(nextDescription);
      setStart(nextStart);
      setEnd(nextEnd);
      setPriority(nextPriority);
      setDynamicFields(fields);
      setBaselineFingerprint(
        editStateFingerprint({
          name: nextName,
          description: nextDescription,
          start: nextStart,
          end: nextEnd,
          priority: nextPriority,
          team: nextTeam,
          dynamicFields: fields,
        })
      );
      return true;
    } catch (error: unknown) {
      if (requestId !== requestRef.current) return false;
      setLoadError(
        isForbiddenError(error)
          ? "You do not have access to view this Deal."
          : parseApiErrorMessage(error, "The Deal details could not be loaded.")
      );
      return false;
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [editable, projectId]);

  useEffect(() => {
    void load();
    return () => {
      requestRef.current += 1;
    };
  }, [load]);

  const retryLoad = async (): Promise<void> => {
    const refreshed = await load();
    if (refreshed) {
      setPartialSaveStatus((current) => (current === "refresh-failed" ? "refreshed" : current));
    }
  };

  const currentUserKey = user?.Id ? `u-${user.Id}` : null;
  const isMultiOwner = project?.IsMultiOwner === true;
  const canEditTeam = !!project && editable && isInitialProjectMember(project, user?.Id);
  const canEditOwners = !!project && editable && isInitialProjectOwner(project, user?.Id);
  const originalOwnerId = project && !isMultiOwner ? singleProjectOwnerId(project) : null;
  const selectedOwnerId = selectedUserOwnerId(options, team);
  const ownerChanged = !isMultiOwner && !!originalOwnerId && !!selectedOwnerId && originalOwnerId !== selectedOwnerId;
  const selectedOwnerName = options.find((option) => option.id === selectedOwnerId && !option.isGroup)?.name;
  const transferLockedMemberKeys = useMemo(
    () => (ownerChanged && originalOwnerId ? new Set([`u-${originalOwnerId}`]) : undefined),
    [originalOwnerId, ownerChanged]
  );
  const ownerNames = useMemo(() => (project ? projectOwnerNames(project) : []), [project]);
  const memberNames = useMemo(() => (project ? projectMemberNames(project) : []), [project]);
  const currentFingerprint = useMemo(
    () =>
      editStateFingerprint({
        name,
        description,
        start,
        end,
        priority,
        team,
        dynamicFields,
      }),
    [description, dynamicFields, end, name, priority, start, team]
  );
  const isDirty = editable && !!baselineFingerprint && currentFingerprint !== baselineFingerprint;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const clearError = (key: string) => setErrors((current) => ({ ...current, [key]: undefined }));

  const applyTeamChange = (next: DealTeamSelection | null, blockedMessage: string) => {
    if (!next) {
      setTeamNotice(blockedMessage);
      return;
    }
    setTeam(next);
    setTeamNotice("");
    clearError("team");
  };

  const validateInformation = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Required";
    if (!parseDateInput(start)) next.start = "Required";
    if (!parseDateInput(end)) next.end = "Required";
    const startDate = parseDateInput(start);
    const endDate = parseDateInput(end);
    if (startDate && endDate && endDate < startDate) next.end = "End date cannot be before Start date";
    const nextDynamic = validateDynamicFields(dynamicFields);
    setErrors(next);
    setDynamicErrors(nextDynamic);
    return Object.keys(next).length === 0 && Object.keys(nextDynamic).length === 0;
  };

  const nextFromInformation = () => {
    if (validateInformation()) setStep(2);
  };

  const toggleAllEditableMembers = (checked: boolean): DealTeamSelection => {
    const next = setAllMembers(options, team, checked, currentUserKey);
    // Orphaned rows are display-only in the Web edit form. Preserve them even
    // when Select all is cleared, because the API replaces the full member set.
    options.filter((option) => option.unavailable).forEach((option) => next.memberKeys.add(option.key));
    transferLockedMemberKeys?.forEach((key) => next.memberKeys.add(key));
    return next;
  };

  const validateBeforeSave = (): boolean => {
    if (!project || !editable) return false;
    if (team.memberKeys.size === 0) {
      setErrors({ team: "Select at least one Deal member." });
      return false;
    }
    if (team.ownerKeys.size === 0) {
      setErrors({ team: "Select at least one Deal owner." });
      return false;
    }
    if (!validateInformation()) {
      setStep(1);
      return false;
    }
    return true;
  };

  const persistDeal = async (transferType?: 0 | 1) => {
    if (!project || !editable) return;
    let editCommitted = false;
    setSaving(true);
    setSubmitError("");
    setPartialSaveStatus(null);
    try {
      const editTeam =
        ownerChanged && originalOwnerId
          ? { memberKeys: new Set(team.memberKeys), ownerKeys: new Set([`u-${originalOwnerId}`]) }
          : team;
      const persistedPayload = buildEditDealPayload({
        project,
        name,
        description,
        start,
        end,
        priority,
        options,
        team: editTeam,
        dynamicFields,
      });
      const updated = await ProjectsApi.editProject(persistedPayload);
      editCommitted = true;

      if (ownerChanged && originalOwnerId && selectedOwnerId) {
        const transferred = await ProjectsApi.transferProjectOwnership({
          ProjectId: persistedPayload.Id,
          OldProjectOwnerId: originalOwnerId,
          NewProjectOwnedId: selectedOwnerId,
          ProjectTransferType: transferType ?? 0,
        });
        if (!transferred) throw new Error("The Deal ownership transfer could not be completed.");
      }

      const finalPayload = ownerChanged
        ? buildEditDealPayload({
            project,
            name,
            description,
            start,
            end,
            priority,
            options,
            team,
            dynamicFields,
          })
        : persistedPayload;
      const merged = {
        ...project,
        ...updated,
        ...finalPayload,
        ...(ownerChanged && selectedOwnerId ? { CreatedById: selectedOwnerId } : {}),
      };
      setBaselineFingerprint(currentFingerprint);
      setProject(merged);
      setTransferDialogOpen(false);
      setSaved(true);
      onUpdated(merged);
    } catch (error: unknown) {
      if (editCommitted && ownerChanged) {
        setTransferDialogOpen(false);
        setStep(1);
        onUpdated(project);
        const refreshed = await load();
        setPartialSaveStatus(refreshed ? "refreshed" : "refresh-failed");
        return;
      }
      setSubmitError(
        isForbiddenError(error)
          ? "You no longer have access to edit this Deal."
          : parseApiErrorMessage(error, "The Deal could not be updated. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!validateBeforeSave()) return;
    if (ownerChanged) {
      setTransferDialogOpen(true);
      return;
    }
    void persistDeal();
  };

  if (loading) {
    return (
      <div className="deal-details__state" role="status">
        <FluentSpinner label="Loading Deal details…" />
      </div>
    );
  }

  if (loadError || !project) {
    return (
      <div className="deal-details__state" role="alert">
        {partialSaveStatus ? (
          <p className="create-deal__submit-error">{partialSaveMessage(partialSaveStatus)}</p>
        ) : null}
        <p>{loadError || "The Deal details could not be loaded."}</p>
        <button type="button" className="create-deal__button" onClick={() => void retryLoad()}>
          Try again
        </button>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="create-deal__success" role="status">
        <CheckmarkCircleRegular className="create-deal__success-icon" />
        <h3>{project.Name || name.trim()} updated</h3>
        <p>The Deal details, team, and owners were saved.</p>
        <div className="create-deal__success-actions">
          <button type="button" className="create-deal__button create-deal__button--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (!editable) {
    return (
      <div className="deal-details">
        <dl className="deal-details__summary">
          <div>
            <dt>Status</dt>
            <dd>{statusName(project)}</dd>
          </div>
          <div>
            <dt>Priority</dt>
            <dd>{project.Priority || "—"}</dd>
          </div>
          <div>
            <dt>Template</dt>
            <dd>{project.ParentTemplateName || "—"}</dd>
          </div>
          <div>
            <dt>Start Date</dt>
            <dd>
              {project.Start || project.StartDate ? formatLongCalendarDate(project.Start ?? project.StartDate) : "—"}
            </dd>
          </div>
          <div>
            <dt>End Date</dt>
            <dd>{project.End || project.EndDate ? formatLongCalendarDate(project.End ?? project.EndDate) : "—"}</dd>
          </div>
        </dl>

        <section className="deal-details__section">
          <h3>Description</h3>
          <p className="deal-details__description">{project.Description || "No description"}</p>
        </section>
        <section className="deal-details__section">
          <h3>{project.IsMultiOwner ? "Owners" : "Owner"}</h3>
          <p>{ownerNames.length > 0 ? ownerNames.join(", ") : "—"}</p>
        </section>
        <section className="deal-details__section">
          <h3>Deal Team</h3>
          <p>{memberNames.length > 0 ? memberNames.join(", ") : "—"}</p>
        </section>
        {dynamicLoadError ? (
          <p className="create-deal__dynamic-load-error" role="alert">
            This Deal&rsquo;s additional fields could not be loaded.
          </p>
        ) : null}
        <DealDynamicFields fields={dynamicFields} errors={{}} onChange={() => {}} disabled />
      </div>
    );
  }

  return (
    <div className="create-deal deal-details__editor">
      <div className="create-deal__body">
        {step === 1 ? (
          <>
            <div className="create-deal__field">
              <label className="create-deal__field-label" htmlFor="edit-deal-name">
                Deal Name <span className="create-deal__required">*</span>
              </label>
              <input
                id="edit-deal-name"
                value={name}
                disabled={saving}
                onChange={(event) => {
                  setName(event.target.value);
                  clearError("name");
                }}
              />
              {errors.name ? <p className="create-deal__field-error">{errors.name}</p> : null}
            </div>

            <div className="create-deal__field">
              <label className="create-deal__field-label" htmlFor="edit-deal-description">
                Description
              </label>
              <textarea
                id="edit-deal-description"
                value={description}
                disabled={saving}
                rows={3}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            <div className="create-deal__grid">
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="edit-deal-start">
                  Start Date <span className="create-deal__required">*</span>
                </label>
                <DealDateInput
                  id="edit-deal-start"
                  value={start}
                  max={end || undefined}
                  disabled={saving}
                  onChange={(event) => {
                    setStart(event.target.value);
                    clearError("start");
                  }}
                />
                {errors.start ? <p className="create-deal__field-error">{errors.start}</p> : null}
              </div>
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="edit-deal-end">
                  End Date <span className="create-deal__required">*</span>
                </label>
                <DealDateInput
                  id="edit-deal-end"
                  value={end}
                  min={start || undefined}
                  disabled={saving}
                  onChange={(event) => {
                    setEnd(event.target.value);
                    clearError("end");
                  }}
                />
                {errors.end ? <p className="create-deal__field-error">{errors.end}</p> : null}
              </div>
            </div>

            <div className="create-deal__grid">
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="edit-deal-template">
                  Template
                </label>
                <input id="edit-deal-template" value={project.ParentTemplateName || "None"} disabled />
              </div>
              <div className="create-deal__field">
                <label className="create-deal__field-label" htmlFor="edit-deal-priority">
                  Priority
                </label>
                <select
                  id="edit-deal-priority"
                  value={priority}
                  disabled={saving}
                  onChange={(event) => setPriority(event.target.value as DealPriority)}
                >
                  {DEAL_PRIORITIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {dynamicLoadError ? (
              <p className="create-deal__dynamic-load-error" role="alert">
                This Deal&rsquo;s additional fields could not be loaded. Existing saved values will be left unchanged.
              </p>
            ) : null}
            <DealDynamicFields
              fields={dynamicFields}
              errors={dynamicErrors}
              disabled={saving}
              onChange={(fieldId, data) => {
                setDynamicFields((current) =>
                  current.map((field) => (field.Id === fieldId ? { ...field, Data: data } : field))
                );
                setDynamicErrors((current) => {
                  if (!current[fieldId]) return current;
                  const next = { ...current };
                  delete next[fieldId];
                  return next;
                });
              }}
              onFilesChange={(fieldId, files) => {
                setDynamicFields((current) =>
                  current.map((field) => (field.Id === fieldId ? { ...field, Files: files } : field))
                );
                setDynamicErrors((current) => {
                  if (!current[fieldId]) return current;
                  const next = { ...current };
                  delete next[fieldId];
                  return next;
                });
              }}
            />
          </>
        ) : null}

        {step === 2 ? (
          <DealTeam
            options={options}
            selection={team}
            search={memberSearch}
            onSearchChange={setMemberSearch}
            onToggleMember={(key) => {
              if (!canEditTeam) return;
              if (transferLockedMemberKeys?.has(key)) {
                setTeamNotice("The current Deal owner remains a member after ownership is transferred.");
                return;
              }
              if (!canEditOwners && initialTeam.ownerKeys.has(key)) {
                setTeamNotice("Only an existing Deal owner can remove an owner from the Deal team.");
                return;
              }
              applyTeamChange(
                toggleMember(team, key, currentUserKey),
                "A Deal needs at least one owner. Add another owner before removing this one."
              );
            }}
            onToggleOwner={(key) => {
              if (!canEditOwners) return;
              applyTeamChange(toggleOwner(options, team, key, isMultiOwner), "A Deal needs at least one owner.");
            }}
            onToggleAll={(checked) => {
              if (canEditTeam) applyTeamChange(toggleAllEditableMembers(checked), "");
            }}
            isMultiOwner={isMultiOwner}
            memberSelectionDisabled={!canEditTeam}
            lockedMemberKeys={transferLockedMemberKeys}
            ownerSelectionDisabled={!canEditOwners}
            showOwnerSelection={canEditOwners}
            disabled={saving}
            notice={teamNotice}
            error={errors.team}
          />
        ) : null}
      </div>

      {transferDialogOpen ? (
        <div className="deal-details__modal-backdrop">
          <section
            className="deal-details__transfer-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deal-transfer-title"
          >
            <h3 id="deal-transfer-title">Transfer Deal Ownership</h3>
            <p>
              You are transferring this Deal to {selectedOwnerName || "the selected owner"}. Choose how templated Task
              ownership should be handled.
            </p>
            <fieldset disabled={saving}>
              <legend>Task ownership</legend>
              <label>
                <input
                  type="radio"
                  name="deal-transfer-type"
                  checked={projectTransferType === 0}
                  onChange={() => setProjectTransferType(0)}
                />
                <span>
                  <strong>Retain Task ownership</strong>
                  Keep ownership of templated Tasks with the current Deal owner.
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="deal-transfer-type"
                  checked={projectTransferType === 1}
                  onChange={() => setProjectTransferType(1)}
                />
                <span>
                  <strong>Transfer Task ownership</strong>
                  Transfer templated Tasks to the new Deal owner as well.
                </span>
              </label>
            </fieldset>
            <div className="deal-details__transfer-actions">
              <button
                type="button"
                className="create-deal__button"
                disabled={saving}
                onClick={() => setTransferDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="create-deal__button create-deal__button--primary"
                disabled={saving}
                onClick={() => void persistDeal(projectTransferType)}
              >
                {saving ? "Saving…" : "Save Transfer"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <div className="create-deal__footer">
        {partialSaveStatus ? (
          <p className="create-deal__submit-error" role="alert">
            {partialSaveMessage(partialSaveStatus)}
          </p>
        ) : null}
        {submitError ? (
          <p className="create-deal__submit-error" role="alert">
            {submitError}
          </p>
        ) : null}
        <div className="create-deal__footer-row">
          <span className="create-deal__progress">
            <span>
              Step {step} of {STEPS.length}
            </span>
            <StepBars step={step} />
          </span>
          <div className="create-deal__footer-actions">
            {step === 1 ? (
              <button type="button" className="create-deal__button" disabled={saving} onClick={onClose}>
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="create-deal__button"
                disabled={saving}
                onClick={() => {
                  setErrors({});
                  setSubmitError("");
                  setStep(1);
                }}
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button
                type="button"
                className="create-deal__button create-deal__button--primary"
                disabled={saving}
                onClick={nextFromInformation}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className="create-deal__button create-deal__button--primary"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? "Saving…" : "Save Deal"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
